import hexToRgb from 'styles/functions/hexToRgb';

import baseColors from './baseColors';
import type { ColorScheme } from './defaultTheme';
import { nodeColors, systemColors } from './defaultTheme';

/**
 * Dark Theme Neutral Colors
 *
 * NOTE: These colors are NOT from design. They are placeholder values
 * until design provides the official dark mode color palette.
 *
 * These semantic constants use the zinc color ramp from baseColors.
 *
 * Light end (text/icons):
 *   zinc["05"]  → #F5F5F5 - Button text
 *   zinc["10"]  → #EDEDED - Primary text/icons
 *   zinc["30"]  → #A1A1AA - Secondary text/icons
 *   zinc["50"]  → #71717A - Helper text, disabled icons
 *   zinc["60"]  → #52525B - Placeholder, disabled text
 *
 * Dark end (backgrounds):
 *   zinc["70"]  → #27272A - Surface grey, borders
 *   zinc["80"]  → #18181B - Dark grey, hover states
 *   zinc["90"]  → #121217 - Card/elevated surfaces
 *   zinc["100"] → #050509 - Page background
 */

// Page and primary backgrounds (darkest)
const DARK_BASE = baseColors.zinc['100'];

// Card and elevated surface backgrounds
const CARD_BLACK = baseColors.zinc['90'];

// Interactive surface backgrounds (hover states, secondary surfaces)
const DARK_GREY = baseColors.zinc['80'];

// Accent surfaces, borders, and elevated elements
const SURFACE_GREY = baseColors.zinc['70'];
const BORDER_GREY = baseColors.zinc['70'];

// Text and icon color tokens
const TEXT_PRIMARY = baseColors.zinc['10'];
const TEXT_SECONDARY = baseColors.zinc['30'];
const TEXT_HELPER = baseColors.zinc['50'];
const TEXT_PLACEHOLDER = baseColors.zinc['60'];
const TEXT_DISABLED = baseColors.zinc['60'];

// Shimmer animation colors (high contrast for visibility on dark backgrounds)
const SHIMMER_DARK = baseColors.zinc['50'];
const SHIMMER_MID = baseColors.white;
const SHIMMER_LIGHT = baseColors.zinc['50'];

export const darkThemeColors = {
  transparent: 'transparent',

  /** BACKGROUND **/
  // Main page background
  backgroundPage: DARK_BASE,

  // Sidebar/Nav background
  backgroundPrimary: DARK_BASE,
  backgroundPrimaryHover: DARK_GREY,

  // Cards and Elevated surfaces
  background: CARD_BLACK,

  // Elements inside Primary (Sidebar)
  backgroundPrimaryInverse: DARK_GREY,
  backgroundPrimaryInverseHover: baseColors.white,

  // Brand surfaces (e.g. buttons)
  backgroundBrand: baseColors.newBrand['80'],
  backgroundBrandInactive: SURFACE_GREY,
  backgroundBrandInverse: baseColors.newBrand['50'],
  backgroundBrandHover: baseColors.newBrand['90'],

  // Accent surfaces (generic neutral pill / icon backgrounds)
  backgroundAccent: SURFACE_GREY,
  backgroundAccentInverse: baseColors.newGrey['90'],

  backgroundInverse: DARK_GREY,
  backgroundInverseHover: SURFACE_GREY,
  backgroundInverseDisabled: SURFACE_GREY,
  backgroundInverseSkeleton: SURFACE_GREY,

  // Generic hover surfaces
  backgroundHover: DARK_GREY,
  backgroundDisabledSubtle: SURFACE_GREY,
  backgroundDisabledStrong: DARK_GREY,
  backgroundSkeleton: SURFACE_GREY,

  backgroundDanger: baseColors.red['90'],
  backgroundDangerHover: baseColors.red['80'],
  backgroundSuccess: baseColors.green['90'],
  backgroundSuccessHover: baseColors.green['80'],
  backgroundWarning: baseColors.yellow['90'],
  backgroundInformation: baseColors.blue['90'],
  backgroundError: baseColors.red['100'],
  backgroundOverlay: hexToRgb(baseColors.black, 0.8),

  /** BORDER **/
  borderPrimary: BORDER_GREY,
  borderBrand: baseColors.newBrand['50'],
  borderBrandHover: baseColors.newBrand['40'],
  borderBrandActive: baseColors.newBrand['40'],
  borderBrandInverse: baseColors.newBrand['50'],
  borderBrandInverseHover: baseColors.newBrand['40'],
  borderBrandInverseActive: baseColors.newBrand['50'],

  borderSubtle: BORDER_GREY,
  borderSubtleInverse: BORDER_GREY,
  borderTable: DARK_GREY,
  borderStrong: SURFACE_GREY,
  borderStrongInverse: SURFACE_GREY,
  borderExtraStrongInverse: baseColors.zinc['60'],
  borderUnderline: SURFACE_GREY,
  borderUnderlineInverse: SURFACE_GREY,

  borderFocus: baseColors.newBrand['50'],
  borderInverseFocus: BORDER_GREY,
  borderDisabled: BORDER_GREY,

  borderDanger: baseColors.red['50'],
  borderSuccess: baseColors.green['50'],
  borderWarning: baseColors.yellow['50'],
  borderInfo: baseColors.blue['50'],

  /** ICON **/
  iconPrimary: TEXT_PRIMARY,
  iconPrimaryHover: baseColors.white,

  iconPrimaryBrand: baseColors.newBrand['50'],

  // Inverse Icons
  iconPrimaryInverse: TEXT_PRIMARY,

  iconSecondary: TEXT_SECONDARY,

  // Secondary Icons
  iconSecondaryInverse: TEXT_SECONDARY,

  iconBrand: baseColors.newBrand['50'],
  iconBrandInverse: baseColors.newBrand['50'],
  iconAccent: baseColors.newBrand['50'],

  iconButtonBrand: baseColors.white,

  iconDisabled: TEXT_HELPER,
  iconInverseDisabled: TEXT_HELPER,

  iconDanger: baseColors.red['50'],
  iconSuccess: baseColors.green['50'],
  iconWarning: baseColors.yellow['50'],
  iconInfo: baseColors.blue['50'],

  /** TEXT **/
  textPrimary: TEXT_PRIMARY,

  // Inverse Text (on Sidebar)
  textPrimaryInverse: TEXT_PRIMARY,

  textSecondary: TEXT_SECONDARY,

  // Secondary Text
  textSecondaryInverse: TEXT_SECONDARY,

  textButtonBrand: baseColors.zinc['05'],

  textHelper: TEXT_HELPER,
  textHelperInverse: TEXT_HELPER,

  textPlaceholder: TEXT_PLACEHOLDER,
  textPlaceholderInverse: TEXT_PLACEHOLDER,

  textBrand: baseColors.newBrand['50'],
  textBrandInverse: baseColors.newBrand['60'],
  textAccent: baseColors.newBrand['50'],

  textDisabled: TEXT_DISABLED,
  textDisabledInverse: TEXT_DISABLED,

  textDanger: baseColors.red['50'],
  textDangerInverse: baseColors.red['40'],
  textSuccess: baseColors.green['50'],
  textSuccessInverse: baseColors.green['40'],
  textWarning: baseColors.yellow['50'],
  textWarningInverse: baseColors.yellow['40'],
  textInfo: baseColors.blue['50'],

  /** COMPONENT-SPECIFIC **/
  // Toggle
  toggleOnBackground: baseColors.newBrand['80'],
  toggleOffBackground: SURFACE_GREY,

  // Brand pill / badge surfaces
  surfaceBrandPill: baseColors.newBrand['70'],

  // Selected project/workspace icon in navigation
  surfaceNavSelection: baseColors.newBrand['70'],

  // GenAI tag background
  genAiTagBackground: SURFACE_GREY,

  // Primary button
  buttonPrimaryBackground: SURFACE_GREY,
  buttonPrimaryBackgroundHover: baseColors.zinc['60'],
  buttonPrimaryText: baseColors.zinc['05'],

  // Slider
  sliderTrackActive: baseColors.newBrand['80'],
  sliderTrackInactive: DARK_GREY,
  sliderThumb: DARK_GREY,
  sliderThumbBorder: SURFACE_GREY,

  /** SHIMMER **/
  textShimmerDark: SHIMMER_DARK,
  textShimmerMid: SHIMMER_MID,
  textShimmerLight: SHIMMER_LIGHT,
};

const darkColors = {
  ...systemColors,
  ...darkThemeColors,
  ...nodeColors,
};

const darkTheme: ColorScheme = {
  colors: darkColors,
  type: 'dark',
};

export default darkTheme;
